import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'livret_read';

type ReadMap = Record<string, string>;

function readMap(): ReadMap {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as ReadMap;
    }
  } catch {
    /* ignore */
  }
  return {};
}

function writeMap(map: ReadMap): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/**
 * Hook pour une section precise du livret. Retourne l etat de lecture et
 * la fonction pour marquer comme lue.
 */
export function useLivretRead(sectionKey: string): {
  isRead: boolean;
  readAt: string | null;
  markAsRead: () => void;
} {
  const [readAt, setReadAt] = useState<string | null>(() => {
    const map = readMap();
    return map[sectionKey] || null;
  });

  // Sync si la valeur change ailleurs (ex: focus window apres edit)
  useEffect(() => {
    const onFocus = (): void => {
      const map = readMap();
      setReadAt(map[sectionKey] || null);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [sectionKey]);

  const markAsRead = useCallback((): void => {
    const map = readMap();
    const ts = new Date().toISOString();
    map[sectionKey] = ts;
    writeMap(map);
    setReadAt(ts);
  }, [sectionKey]);

  return {
    isRead: readAt !== null,
    readAt,
    markAsRead,
  };
}

/**
 * Hook pour le hub : lit toutes les sections deja lues d un coup.
 */
export function useLivretReadAll(): {
  readSections: Set<string>;
  refresh: () => void;
} {
  const [readSections, setReadSections] = useState<Set<string>>(() => new Set(Object.keys(readMap())));

  const refresh = useCallback((): void => {
    setReadSections(new Set(Object.keys(readMap())));
  }, []);

  useEffect(() => {
    const onFocus = (): void => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  return { readSections, refresh };
}
